/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Stack } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { StatCardV2 } from '../../../components/v2';
import TableService from '../../../services/TableService';
import VencimentoStrip from './VencimentoStrip';
import {
  getGridSizeForPipelineCount,
  getGroupCardValues,
  getPipelineGroupsForPersona,
  showVencimentoStripForPersona,
} from './pipelineConfig';

const EMPTY_RESUMO = {
  filtrado: {},
  total: {},
  vencimento: null,
};

const ICON_BY_COLOR = {
  info: <InfoOutlinedIcon />,
  warning: <WarningAmberOutlinedIcon />,
  success: <CheckCircleOutlineIcon />,
  error: <ErrorOutlineIcon />,
  secondary: <AssignmentOutlinedIcon />,
};

const normalizeResumo = (data) => {
  if (!data || typeof data !== 'object') {
    return EMPTY_RESUMO;
  }

  if (data.filtrado != null || data.total != null || data.vencimento != null) {
    return {
      filtrado: data.filtrado && typeof data.filtrado === 'object' ? data.filtrado : {},
      total: data.total && typeof data.total === 'object' ? data.total : {},
      vencimento: data.vencimento && typeof data.vencimento === 'object' ? data.vencimento : null,
    };
  }

  return { filtrado: data, total: {}, vencimento: null };
};

const ResumoSection = ({
  apiFilter,
  persona,
  activePipelineGroup,
  onPipelineGroupChange,
  activeUrgency,
  onUrgencyChange,
  refreshKey,
}) => {
  const [resumo, setResumo] = useState(EMPTY_RESUMO);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const pipelineGroups = useMemo(
    () => getPipelineGroupsForPersona(persona),
    [persona],
  );

  const gridSize = useMemo(
    () => getGridSizeForPipelineCount(pipelineGroups.length),
    [pipelineGroups.length],
  );

  const showVencimento = showVencimentoStripForPersona(persona);

  const fetchResumo = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    try {
      const response = await TableService.request('nsirs/resumo', apiFilter);
      if (requestId !== requestIdRef.current) return;
      setResumo(normalizeResumo(response?.data));
    } catch {
      if (requestId !== requestIdRef.current) return;
      setResumo(EMPTY_RESUMO);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiFilter, refreshKey]);

  useEffect(() => {
    fetchResumo();
  }, [fetchResumo]);

  return (
    <Stack spacing={1.5}>
      <Grid container spacing={2}>
        {pipelineGroups.map((group) => {
          const cardValues = getGroupCardValues(resumo.filtrado, resumo.total, group);
          return (
            <Grid key={group.key} size={gridSize}>
              <StatCardV2
                label={group.label}
                valueFiltered={cardValues.valueFiltered}
                valueTotal={cardValues.valueTotal}
                totalCaption="no total"
                icon={ICON_BY_COLOR[group.color] || ICON_BY_COLOR.info}
                color={group.color}
                loading={loading}
                active={activePipelineGroup === group.key}
                onClick={() => {
                  onPipelineGroupChange(
                    activePipelineGroup === group.key ? null : group.key,
                  );
                }}
              />
            </Grid>
          );
        })}
      </Grid>

      {showVencimento && (
        <VencimentoStrip
          summary={resumo.vencimento}
          activeUrgency={activeUrgency}
          onUrgencyChange={onUrgencyChange}
          loading={loading}
        />
      )}
    </Stack>
  );
};

ResumoSection.propTypes = {
  apiFilter: PropTypes.object.isRequired,
  persona: PropTypes.string.isRequired,
  activePipelineGroup: PropTypes.string,
  onPipelineGroupChange: PropTypes.func.isRequired,
  activeUrgency: PropTypes.string,
  onUrgencyChange: PropTypes.func,
  refreshKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
};

ResumoSection.defaultProps = {
  activePipelineGroup: null,
  activeUrgency: null,
  onUrgencyChange: null,
  refreshKey: false,
};

export default ResumoSection;
